import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
  isExtremeCase,
  mapUpload,
  MediaUpload,
  selectUploadFile,
} from '@wellesley/uploads'
import { platformSettingsSlice } from '@wellesley/settings'
import { EXTREME_VIDEO_BREAKPOINT } from 'components/Video'

type UseSingleMediaProps = {
  upload: MediaUpload
  containerWidth: number
  isReply?: boolean
}

const THRESHOLD = 0.5

const firstLevelReplyWidth = 560
const replyLeftPadding = 44
// two reply spaces + img space
const thirdReplyWidth = firstLevelReplyWidth - replyLeftPadding * 3

const useSingleMedia = ({
  upload: uploadFromProps,
  containerWidth,
  isReply,
}: UseSingleMediaProps) => {
  const videoExtensions = useSelector(
    platformSettingsSlice.selectors.selectVideoExtensions,
  )

  return useMemo(() => {
    const upload = mapUpload(uploadFromProps)
    const isVideo = upload?.uploadType?.toUpperCase() === 'VIDEO'

    const bestFitUploadFile = selectUploadFile({
      upload,
      allowedExtensions: isVideo ? videoExtensions : undefined,
      size: containerWidth,
    })

    const width = bestFitUploadFile?.meta.width || 0
    const height = bestFitUploadFile?.meta.height || 0
    const ratio = width / height

    const isVertical = height > width
    const isExtremeVideo = isVideo ? isExtremeCase(bestFitUploadFile) : false

    const uploadSizes = isExtremeVideo
      ? {
          height: EXTREME_VIDEO_BREAKPOINT,
          width: EXTREME_VIDEO_BREAKPOINT * ratio,
        }
      : {
          width: bestFitUploadFile?.meta.width || 0,
          height: bestFitUploadFile?.meta.height || 0,
        }

    if (isReply) {
      if (isVertical) {
        return {
          width: firstLevelReplyWidth / 2,
          height: firstLevelReplyWidth / 2 / ratio,
        }
      } else {
        return { width: thirdReplyWidth, height: thirdReplyWidth / ratio }
      }
    }

    const isNativeFit = uploadSizes.width <= containerWidth

    if (isNativeFit) {
      return uploadSizes
    } else {
      const scale = containerWidth / uploadSizes.width
      const newWidth = uploadSizes.width * scale
      const newHeight = uploadSizes.height * scale
      const maxHeight = screen.height * THRESHOLD

      if (newHeight <= maxHeight) {
        return { width: newWidth, height: newHeight }
      } else {
        const scale = maxHeight / newHeight
        return { width: newWidth * scale, height: newHeight * scale }
      }
    }
  }, [uploadFromProps, containerWidth, videoExtensions, isReply])
}

export default useSingleMedia
